import { TranslocoService } from '@jsverse/transloco';
import { ActivityMetadata } from './activity-metadata';
import { ZodError } from 'zod';
import { gameIdToName } from 'src/app/shared/models/game/game-id.utils';
import { ThemeService } from '../../theme/theme.service';
import {
  ActivityData,
  ActivityDrawData,
  ActivityDrawDataSchema,
  ActivityMessageData,
  ActivityNewPlayerData,
  ActivityType,
  ActivityNewPlayerDataSchema,
  ActivityReferralRewardData,
  ActivityReferralRewardDataSchema,
  ActivitySoloWinData,
  ActivitySoloWinDataSchema,
  ActivityWinData,
  ActivityWinDataSchema,
} from 'src/app/core/api/player/schemas/activity-feed';
import { JwtCurrencyData } from '../../profile/profile.service';
import {
  CurrencyAmountDisplay,
  convertToUserCurrency,
} from '../../../../shared/utils/currency/currency-format';
import { CurrencyType } from '../../../../shared/utils/currency/currency-type';

export class ActivityUtilities {
  static isDuelWinData(data: ActivityData): data is ActivityWinData {
    return ActivityWinDataSchema.safeParse(data).success;
  }

  static isDrawData(data: ActivityData): data is ActivityDrawData {
    return ActivityDrawDataSchema.safeParse(data).success;
  }

  static isSoloWinData(data: ActivityData): data is ActivitySoloWinData {
    return ActivitySoloWinDataSchema.safeParse(data).success;
  }

  static isNewPlayerData(data: ActivityData): data is ActivityNewPlayerData {
    return ActivityNewPlayerDataSchema.safeParse(data).success;
  }

  static isReferralRewardData(data: ActivityData): data is ActivityReferralRewardData {
    return ActivityReferralRewardDataSchema.safeParse(data).success;
  }

  // A paid win, duel or Solo: glows as a win, leads the loading-screen hints and survives the
  // anonymous filter.
  static isWinActivity(type: ActivityType): boolean {
    return type === ActivityType.Win || type === ActivityType.SoloWin;
  }

  // An activity's amount is in match currency; every surface shows it in the player's.
  static convertReward(
    amount: number,
    jwtCurrency: JwtCurrencyData,
  ): { currency: CurrencyType; amount: number } {
    return convertToUserCurrency(jwtCurrency.matchCurrency, amount, {
      userCurrency: jwtCurrency.userCurrency,
      usdExchangeRate: jwtCurrency.exchangeRate,
    });
  }

  static formatReward(
    amount: number,
    jwtCurrency: JwtCurrencyData,
    themeService: ThemeService,
  ): CurrencyAmountDisplay {
    const converted = ActivityUtilities.convertReward(amount, jwtCurrency);
    return themeService.formatCurrencyAmount(converted.currency, converted.amount);
  }

  static transformDisplayDates(
    activities: ActivityMessageData[],
    now: number,
    displayBufferInterval: number,
  ): ActivityMessageData[] {
    if (activities.length === 0) {
      return [];
    }

    return activities.map((activity) => ({
      ...activity,
      // Only set displayedAt if it doesn't already exist
      displayedAt: activity.displayedAt ?? activity.createdAt + displayBufferInterval,
    }));
  }

  static extractActivityMetadata(
    activity: ActivityMessageData,
    translocoService: TranslocoService,
    themeService: ThemeService,
    jwtCurrency: JwtCurrencyData,
  ): ActivityMetadata {
    try {
      if (ActivityUtilities.isDuelWinData(activity.data)) {
        const currencyReward = ActivityUtilities.formatReward(
          activity.data.amount,
          jwtCurrency,
          themeService,
        );

        if (themeService.isAnonymous()) {
          return {
            icon: 'trophy',
            iconColor: '--game-win',
            message: activity.data.winnerNick,
            actionMessage: translocoService.translate('pages.activity-feed.win'),
            actionReward: currencyReward,
          };
        } else {
          const gameId = activity.data.gameId;
          const score = `(${themeService.formatNumber(
            activity.data.winnerScore,
          )} - ${themeService.formatNumber(activity.data.loserScore)})`;
          const opponents = translocoService.translate('pages.activity-feed.win-activity', {
            winnerNick: activity.data.winnerNick,
            loserNick: activity.data.loserNick,
            gameName: gameIdToName(gameId, translocoService),
          });

          return {
            icon: 'trophy',
            iconColor: '--game-win',
            message: `${opponents} ${score}`,
            actionMessage: translocoService.translate('pages.activity-feed.win'),
            actionReward: currencyReward,
          };
        }
      } else if (ActivityUtilities.isDrawData(activity.data)) {
        const currencyReward = ActivityUtilities.formatReward(
          activity.data.amount,
          jwtCurrency,
          themeService,
        );

        const gameId = activity.data.gameId;
        const score = `(${themeService.formatNumber(
          activity.data.score,
        )} - ${themeService.formatNumber(activity.data.score)})`;
        const opponents = translocoService.translate('pages.activity-feed.draw-activity', {
          firstPlayerNick: activity.data.firstPlayerNick,
          secondPlayerNick: activity.data.secondPlayerNick,
          gameName: gameIdToName(gameId, translocoService),
        });

        return {
          icon: 'flag',
          iconColor: '--game-draw',
          message: `${opponents} ${score}`,
          actionMessage: translocoService.translate('pages.activity-feed.draw'),
          actionReward: currencyReward,
        };
      } else if (ActivityUtilities.isSoloWinData(activity.data)) {
        const currencyReward = ActivityUtilities.formatReward(
          activity.data.amount,
          jwtCurrency,
          themeService,
        );

        // The target icon must match the Solo result drawer's mode pill. The player's score is
        // appended outside the copy, as a duel's score pair is; the target is never shown.
        if (themeService.isAnonymous()) {
          return {
            icon: 'target',
            iconColor: '--game-win',
            message: activity.data.nick,
            actionMessage: translocoService.translate('pages.activity-feed.win'),
            actionReward: currencyReward,
          };
        }
        const outscored = translocoService.translate('pages.activity-feed.solo-win-activity', {
          nick: activity.data.nick,
          gameName: gameIdToName(activity.data.gameId, translocoService),
        });
        return {
          icon: 'target',
          iconColor: '--game-win',
          message: `${outscored} (${themeService.formatNumber(activity.data.score)})`,
          actionMessage: translocoService.translate('pages.activity-feed.win'),
          actionReward: currencyReward,
        };
      } else if (ActivityUtilities.isNewPlayerData(activity.data)) {
        const message = translocoService.translate('pages.activity-feed.new-player-activity', {
          nick: activity.data.nick,
        });
        return {
          icon: 'rocket-launch',
          iconColor: '--grey-white',
          message,
        };
      } else if (ActivityUtilities.isReferralRewardData(activity.data)) {
        const rewardCountTranslations = new Map<number, string>([
          [1, 'pages.activity-feed.referral-1'],
          [10, 'pages.activity-feed.referral-10'],
          [20, 'pages.activity-feed.referral-20'],
          [50, 'pages.activity-feed.referral-50'],
          [100, 'pages.activity-feed.referral-100'],
          [200, 'pages.activity-feed.referral-200'],
          [500, 'pages.activity-feed.referral-500'],
          [1000, 'pages.activity-feed.referral-1000'],
          [2000, 'pages.activity-feed.referral-2000'],
          [5000, 'pages.activity-feed.referral-5000'],
          [10000, 'pages.activity-feed.referral-10000'],
          [20000, 'pages.activity-feed.referral-20000'],
          [50000, 'pages.activity-feed.referral-50000'],
          [100000, 'pages.activity-feed.referral-100000'],
        ]);

        const congratulationsKey =
          rewardCountTranslations.get(activity.data.rewardsCount) ||
          'pages.activity-feed.referral-10';
        const congratulations = translocoService.translate(congratulationsKey, {
          nick: activity.data.nick,
        });

        const referralsPaymentsKey =
          activity.data.rewardsCount === 1
            ? 'pages.activity-feed.single-referral'
            : 'pages.activity-feed.multiple-referrals';
        const referralsPayments = translocoService.translate(referralsPaymentsKey, {
          nick: activity.data.nick,
          amount: themeService.formatNumber(activity.data.rewardsCount),
        });

        return {
          icon: 'dollar-meeting',
          iconColor: '--grey-white',
          message: `${congratulations} ${referralsPayments}`,
        };
      }
    } catch (error) {
      if (error instanceof ZodError) {
        // Handle the ZodError here
        console.error('Zod Validation Error: ', error.issues);
      }
    }
    throw new Error(`Invalid activity type: ${JSON.stringify(activity)}`);
  }

  static formatTimeInterval(now: Date, displayedAt: Date | undefined): string {
    if (displayedAt === undefined) {
      return `Just now`;
    }

    const secondsInMinute = 60;
    const secondsInHour = 3600; // 60 * 60
    const secondsInDay = 86400; // 60 * 60 * 24

    const interval = Math.floor((now.getTime() - displayedAt.getTime()) / 1000);

    if (interval < secondsInMinute) {
      return `Just now`;
    } else if (interval < secondsInHour) {
      const minutes = Math.floor(interval / secondsInMinute);
      return `${minutes}m ago`;
    } else if (interval < secondsInDay) {
      const hours = Math.floor(interval / secondsInHour);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(interval / secondsInDay);
      return `${days}d ago`;
    }
  }
}
